import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GaugeIcon, ScanLineIcon, LockIcon, CheckCircle2Icon } from "lucide-react";

export default function Envase() {
  const [receipts, setReceipts] = useState([]);
  const [remessa, setRemessa] = useState("");
  const [queue, setQueue] = useState([]);
  const [activeReceipt, setActiveReceipt] = useState(null);
  const [form, setForm] = useState({ no_serie: "", lacre_1: "", lacre_2: "", lacre_3: "", qtd_envasada: "", no_lote_manual: "" });
  const [loteAuto, setLoteAuto] = useState(true);
  const [searchSerie, setSearchSerie] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/bulk-receipts");
      setReceipts(data.filter((r) => ["PRONTO_DESCARGA", "EM_ENVASE"].includes(r.status)));
    })();
  }, []);

  const loadQueue = async (rem) => {
    setRemessa(rem);
    if (!rem) return setQueue([]);
    try {
      const { data } = await api.get(`/containers-fefo/${rem}`);
      setQueue(data.queue);
      setActiveReceipt(data.remessa);
    } catch {
      setQueue([]);
    }
  };

  const pickSerie = (no) => setForm((f) => ({ ...f, no_serie: no }));

  const submit = async (e) => {
    e.preventDefault();
    if (!remessa) return toast.error("Selecione a remessa");
    setLoading(true);
    try {
      const { data } = await api.post("/envase", {
        remessa,
        no_serie: form.no_serie,
        lacre_1: form.lacre_1,
        lacre_2: form.lacre_2,
        lacre_3: form.lacre_3,
        qtd_envasada: parseFloat(form.qtd_envasada),
        no_lote_manual: loteAuto ? null : (form.no_lote_manual || null),
      });
      toast.success(`Envase concluído · Lote ${data.no_lote}`);
      if (data.alerta) toast.warning(data.alerta);
      setForm({ no_serie: "", lacre_1: "", lacre_2: "", lacre_3: "", qtd_envasada: "", no_lote_manual: "" });
      loadQueue(remessa);
      const r = await api.get("/bulk-receipts");
      setReceipts(r.data.filter((x) => ["PRONTO_DESCARGA", "EM_ENVASE"].includes(x.status)));
      if (activeReceipt) {
        const updated = r.data.find((x) => x.remessa === remessa);
        if (updated) setActiveReceipt(updated);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const progress = activeReceipt && activeReceipt.volume_calculado
    ? Math.min(100, ((activeReceipt.volume_envasado || 0) / activeReceipt.volume_calculado) * 100)
    : 0;

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Tela 03 · Linha de produção</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Linha de Envase</h1>
          <p className="text-sm text-muted-foreground mt-2">Fracionamento granel → contentores. FEFO automático e validação de triplos lacres.</p>
        </div>
        <GaugeIcon className="w-12 h-12 text-[#FFD60A]" strokeWidth={1.2} />
      </header>

      {/* Remessa selector + progress */}
      <Card className="bg-[#14151A] border-border rounded-none p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Remessa ativa</Label>
            <Select value={remessa} onValueChange={loadQueue}>
              <SelectTrigger data-testid="envase-remessa" className="mt-1 rounded-none bg-[#0A0A0C] h-12 border-border">
                <SelectValue placeholder="Selecione a remessa em descarga" />
              </SelectTrigger>
              <SelectContent className="bg-[#14151A] border-border rounded-none">
                {receipts.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nenhuma remessa liberada</div>}
                {receipts.map((r) => (
                  <SelectItem key={r.id} value={r.remessa} data-testid={`opt-${r.remessa}`}>
                    <span className="font-mono text-[#FFD60A] mr-2">{r.remessa}</span>
                    {r.nome_produto}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {activeReceipt && (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Produto · será herdado pelo contentor</Label>
                <div className="font-display text-lg font-bold leading-tight mt-1">{activeReceipt.nome_produto}</div>
                <div className="text-xs text-[#00E5FF] font-mono mt-1">
                  <span className="text-[#FFD60A] mr-2">#{activeReceipt.codigo_produto}</span>
                  {activeReceipt.funcao_quimica}
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Progresso do envase</Label>
                <div className="mt-2 h-3 bg-[#0A0A0C] border border-border relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-[#FFD60A] transition-all" style={{ width: `${progress}%` }} />
                </div>
                <div className="flex justify-between mt-1 text-xs font-mono">
                  <span className="text-[#FFD60A]">{(activeReceipt.volume_envasado || 0).toFixed(0)} L</span>
                  <span className="text-muted-foreground">{activeReceipt.volume_calculado.toFixed(0)} L · {progress.toFixed(1)}%</span>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* FEFO queue */}
        <Card className="lg:col-span-2 bg-[#0A0A0C] border-border rounded-none p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-[#14151A]">
            <div className="text-[11px] uppercase tracking-widest text-[#00E5FF]">Fila FEFO sugerida</div>
            <div className="text-xs text-muted-foreground mt-0.5">Quem vence primeiro envasa primeiro</div>
            <input
              data-testid="envase-fefo-search"
              value={searchSerie}
              onChange={(e) => setSearchSerie(e.target.value.toUpperCase())}
              placeholder="Buscar N° Série na fila..."
              className="mt-2 w-full rounded-none bg-[#0A0A0C] border border-border h-9 px-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#FFD60A]"
            />
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {queue.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground">Selecione uma remessa para visualizar a fila.</div>
            )}
            {queue.filter((c) => !searchSerie || c.no_serie.includes(searchSerie)).map((c, i) => {
              const minDate = [c.data_teste_carga, c.data_hidrostatico, c.data_estanqueidade].sort()[0];
              return (
                <button
                  key={c.no_serie}
                  onClick={() => pickSerie(c.no_serie)}
                  data-testid={`fefo-${c.no_serie}`}
                  className={`w-full text-left border-b border-border/60 px-4 py-3 hover:bg-[#14151A] transition-colors ${form.no_serie === c.no_serie ? "bg-[#FFD60A]/10 border-l-2 border-l-[#FFD60A]" : "border-l-2 border-l-transparent"}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-muted-foreground">#{String(i+1).padStart(2,"0")}</span>
                      <span className="font-mono text-[#FFD60A] font-bold">{c.no_serie}</span>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">{c.capacidade}L</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1 ml-7 font-mono">Próx. venc.: <span className="text-[#FF9F0A]">{minDate}</span></div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Envase form */}
        <Card className="lg:col-span-3 bg-[#14151A] border-border rounded-none p-6">
          <div className="flex items-center gap-2 mb-4">
            <ScanLineIcon className="w-5 h-5 text-[#FFD60A]" />
            <h2 className="font-display text-2xl font-bold">Confirmar envase</h2>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">N° Série bipado</Label>
              <Input
                data-testid="envase-serie"
                value={form.no_serie}
                onChange={(e) => setForm({ ...form, no_serie: e.target.value.toUpperCase() })}
                required
                placeholder="Bipa ou clique no contentor da fila"
                className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-14 text-lg border-l-4 border-l-[#FFD60A]"
              />
            </div>

            <div className="border border-border p-4 bg-[#0A0A0C]">
              <div className="flex items-center gap-2 mb-3">
                <LockIcon className="w-4 h-4 text-[#FF9F0A]" />
                <span className="text-xs uppercase tracking-widest text-[#FF9F0A]">Triplo lacre obrigatório</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((n) => (
                  <div key={n}>
                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lacre #{n}</Label>
                    <Input
                      data-testid={`envase-lacre-${n}`}
                      value={form[`lacre_${n}`]}
                      onChange={(e) => setForm({ ...form, [`lacre_${n}`]: e.target.value })}
                      required
                      className="mt-1 rounded-none bg-[#14151A] font-mono h-11 text-center"
                      placeholder={["341051","341081","353469"][n-1]}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Qtd. Envasada (L)</Label>
              <Input
                data-testid="envase-qtd"
                type="number"
                step="0.01"
                value={form.qtd_envasada}
                onChange={(e) => setForm({ ...form, qtd_envasada: e.target.value })}
                required
                placeholder="5000"
                className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-12"
              />
            </div>

            {/* N° do Lote — auto OR manual */}
            <div className="border border-border p-3 bg-[#0A0A0C]">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">N° do Lote</Label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={loteAuto}
                    onChange={(e) => setLoteAuto(e.target.checked)}
                    data-testid="envase-lote-auto"
                    className="accent-[#FFD60A]"
                  />
                  <span className="text-[10px] uppercase tracking-widest text-[#FFD60A]">Gerar Lote Automático</span>
                </label>
              </div>
              <Input
                data-testid="envase-lote-manual"
                value={loteAuto ? "(gerado na confirmação · L<aammdd>-<últ4>)" : form.no_lote_manual}
                onChange={(e) => setForm({ ...form, no_lote_manual: e.target.value.toUpperCase() })}
                disabled={loteAuto}
                placeholder="Digite o lote manualmente"
                className="rounded-none bg-[#14151A] font-mono h-11 disabled:opacity-60"
              />
            </div>

            <Button type="submit" disabled={loading || !remessa} data-testid="envase-submit" className="w-full h-16 rounded-none bg-[#FFD60A] text-black hover:bg-[#ffdf3a] font-bold uppercase tracking-wider text-lg">
              <CheckCircle2Icon className="w-5 h-5 mr-2" />
              {loading ? "Selando..." : (loteAuto ? "Concluir envase & gerar lote" : "Concluir envase (lote manual)")}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
